import { Badge } from "@heroui/react";
import i18n from "@/i18n";

import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Tables } from "@/gen/tableNames/tables";
import { formatNumber } from "@/features/Table/utils";
import { OptionList } from "@/types/optionList";
import { subAssetClassOptions } from "./filter";

export const assetClassOptions = new OptionList(
  { value: "0", label: "Fx" },
  { value: "1", label: "Equity" },
  { value: "2", label: "Fixed Income" },
  { value: "3", label: "Commodities" },
  { value: "4", label: "Cash" },
  { value: "5", label: "Credit" },
  { value: "6", label: "Multi Asset" },
  { value: "7", label: "Na" },
  { value: "8", label: "Cryptocurrency" },
  { value: "9", label: "Real Estate" },
  { value: "10", label: "Private Equity" },
  { value: "11", label: "Private Debt" },
  { value: "12", label: "Infrastructure" },
);

// Define the F1StrategyMetadata type
export type F1StrategyMetadata = {
  id: number;
  strategy_id: number;
  strategy_name?: string; // Added for association display
  isin: string | null;
  company_name: string | null;
  asset_class: number | null;
  commitment_fgao: number | null;
  commitment_fgti: number | null;
  commitment_fgao_rentes: number | null;
  dedicated_fund: boolean | null;
  exemption_comment: string | null;
  sub_asset_class: string | null;
  fund_esg_rating_level: number | null;
  created_at: string;
  updated_at: string;
};

const t = (key: string, defaultValue?: string) => {
  return i18n.t(`${Tables.F1_STRATEGY_METADATA}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });
};

// Define the columns for the table
export const f1_strategy_metadata_columns: ColumnDef<F1StrategyMetadata>[] = [
  {
    accessorKey: "id",
    header: () => t("id"),
    cell: ({ row }) => {
      const id = row.getValue("id") as number;
      return (
        <Link
          to={toWorkspacePath(`/admin/${Tables.F1_STRATEGY_METADATA}/${id}`)}
          className="text-blue-600 hover:text-blue-800"
        >
          {id}
        </Link>
      );
    },
  },
  {
    accessorKey: "strategy_name",
    header: () => t("strategy_name"),
  },
  {
    accessorKey: "isin",
    header: () => t("isin"),
  },
  {
    accessorKey: "company_name",
    header: () => t("company_name"),
  },
  {
    accessorKey: "asset_class",
    header: () => t("asset_class"),
    cell: ({ row }) => assetClassOptions.getLabel(row.getValue("asset_class")),
  },
  {
    accessorKey: "dedicated_fund",
    header: () => t("dedicated_fund"),
    cell: ({ row }) => {
      const value = row.getValue("dedicated_fund");
      return value === true ? (
        <Badge color="success">Oui</Badge>
      ) : value === false ? (
        <Badge color="danger">Non</Badge>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    accessorKey: "sub_asset_class",
    header: () => t("sub_asset_class"),
    cell: ({ row }) =>
      subAssetClassOptions.getLabel(row.getValue("sub_asset_class")),
  },
  {
    accessorKey: "fund_esg_rating_level",
    header: () => t("fund_esg_rating_level"),
  },
  {
    accessorKey: "commitment_fgao",
    header: () => t("commitment_fgao"),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("commitment_fgao"))}
        </div>
      );
    },
  },
  {
    accessorKey: "commitment_fgti",
    header: () => t("commitment_fgti"),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("commitment_fgti"))}
        </div>
      );
    },
  },
  {
    accessorKey: "commitment_fgao_rentes",
    header: () => t("commitment_fgao_rentes"),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("commitment_fgao_rentes"))}
        </div>
      );
    },
  },
  {
    accessorKey: "exemption_comment",
    header: () => t("exemption_comment"),
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Link
          to={toWorkspacePath(
            `/admin/${Tables.F1_STRATEGY_METADATA}/${row.getValue("id")}`,
          )}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
